import React, { useState, useEffect } from 'react';
import { Grid } from 'components/mui';
import { CaseFormData } from 'containers/pages/cases/types';
import { searchGroups, searchProjects, searchDeliverables, GPDOption } from 'api/cases/searchGPD';
import FormAutocomplete, { ensureOption } from 'containers/pages/cases/components/caseForm/FormAutocomplete';
import FormSection from 'containers/pages/cases/components/caseForm/FormSection';

interface GPDSectionProps {
	form: CaseFormData;
	onGroupChange: (_event: unknown, value: GPDOption | null) => void;
	onProjectChange: (_event: unknown, value: GPDOption | null) => void;
	onDeliverableChange: (_event: unknown, value: GPDOption | null) => void;
}

const GPDSection: React.FC<GPDSectionProps> = ({
	form,
	onGroupChange,
	onProjectChange,
	onDeliverableChange,
}) => {
	const [groups, setGroups] = useState<GPDOption[]>([]);
	const [projects, setProjects] = useState<GPDOption[]>([]);
	const [deliverables, setDeliverables] = useState<GPDOption[]>([]);

	useEffect(() => {
		if (form.CustomerId) {
			searchGroups(form.CustomerId, '').then(setGroups).catch(() => {});
		}
	}, [form.CustomerId]);

	useEffect(() => {
		if (form.GroupId) {
			searchProjects(form.GroupId, '').then(setProjects).catch(() => {});
		}
	}, [form.GroupId]);

	useEffect(() => {
		if (form.ProjectId) {
			searchDeliverables(form.ProjectId, '').then(setDeliverables).catch(() => {});
		}
	}, [form.ProjectId]);

	const groupValue: GPDOption | null = form.GroupId ? { Id: form.GroupId, Title: form.GroupTitle } : null;
	const projectValue: GPDOption | null = form.ProjectId ? { Id: form.ProjectId, Title: form.ProjectTitle } : null;
	const deliverableValue: GPDOption | null = form.DeliverableId ? { Id: form.DeliverableId, Title: form.DeliverableTitle } : null;

	const groupOptions = ensureOption(groups, groupValue);
	const projectOptions = ensureOption(projects, projectValue);
	const deliverableOptions = ensureOption(deliverables, deliverableValue);

	const filledCount = [form.GroupId, form.ProjectId, form.DeliverableId].filter(Boolean).length;

	return (
		<FormSection
			title="Group / Project / Deliverable"
			collapsible
			defaultExpanded
			fieldCount={filledCount}
		>
			<Grid container spacing={2}>
				<Grid item xs={12} sm={4}>
					<FormAutocomplete
						label="Group"
						options={groupOptions}
						value={groupValue}
						onChange={onGroupChange}
						getOptionLabel={(opt) => opt.Title}
						onSearch={(val) => {
							if (form.CustomerId) searchGroups(form.CustomerId, val).then(setGroups).catch(() => {});
						}}
						disabled={!form.CustomerId}
						helperText={!form.CustomerId ? 'Select a customer first' : ''}
					/>
				</Grid>
				<Grid item xs={12} sm={4}>
					<FormAutocomplete
						label="Project"
						options={projectOptions}
						value={projectValue}
						onChange={onProjectChange}
						getOptionLabel={(opt) => opt.Title}
						onSearch={(val) => {
							if (form.GroupId) searchProjects(form.GroupId, val).then(setProjects).catch(() => {});
						}}
						disabled={!form.GroupId}
						helperText={!form.GroupId ? 'Select a group first' : ''}
					/>
				</Grid>
				<Grid item xs={12} sm={4}>
					<FormAutocomplete
						label="Deliverable"
						options={deliverableOptions}
						value={deliverableValue}
						onChange={onDeliverableChange}
						getOptionLabel={(opt) => opt.Title}
						onSearch={(val) => {
							if (form.ProjectId) searchDeliverables(form.ProjectId, val).then(setDeliverables).catch(() => {});
						}}
						disabled={!form.ProjectId}
						helperText={!form.ProjectId ? 'Select a project first' : ''}
					/>
				</Grid>
			</Grid>
		</FormSection>
	);
};

export default GPDSection;
